import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Navigate, useSearchParams } from 'react-router-dom';
import Icon from '../Shared/Icons';
import { useAuth } from '../../context/AuthContext';
import { getProgram, blocksFor, blockOverride, MEMBER_TYPES } from '../../config/programs';
import { BLOCKS, COMMON_FIELDS, RATING_LABELS, completionOf } from '../../utils/reportSchema';
import {
  fetchMember, fetchReportById, saveIndividualReport, addContribution,
  individualReportId, resolveAuthorName,
} from '../../utils/programData';
import { isoWeekKey, weekLabel, weekKeyToDate, fmtDateTime } from '../../utils/weekUtils';
import { draftFromNotes } from '../../utils/programAI';
import { ProgramShell, Field, BlockSection, S, T } from './ui';

export default function IndividualReport() {
  const { pid, mid } = useParams();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { currentUser } = useAuth();
  const program = getProgram(pid);

  const wk = params.get('week') || isoWeekKey();
  const [member, setMember] = useState(null);
  const [existing, setExisting] = useState(null);
  const [values, setValues] = useState({});
  const [open, setOpen] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  // Nyongeza (contribution) ya staff mwingine
  const [contrib, setContrib] = useState('');
  const [readOnly, setReadOnly] = useState(false);

  // AI
  const [notes, setNotes] = useState('');
  const [aiBusy, setAiBusy] = useState(false);
  const [showAI, setShowAI] = useState(false);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [pid, mid, wk]);

  async function load() {
    setLoading(true);
    try {
      const m = await fetchMember(mid);
      setMember(m);
      const r = await fetchReportById(individualReportId(pid, mid, wk));
      if (r) {
        setExisting(r);
        setValues(r.values || {});
        setReadOnly(r.authorId !== currentUser?.uid);
        const ids = blocksFor(pid, m?.type);
        const o = {};
        ids.forEach(b => { o[b] = true; });
        setOpen(o);
      } else {
        setValues({ chessLevel: m?.chessLevel || '' });
        const ids = blocksFor(pid, m?.type);
        setOpen({ [ids[0]]: true });
      }
    } catch (e) { console.error(e); setErr('Imeshindikana kupakia ripoti.'); }
    setLoading(false);
  }

  if (!program) return <Navigate to="/programs" />;

  const blockIds = blocksFor(pid, member?.type);
  const visibleBlocks = blockIds
    .map(id => ({ id, ...BLOCKS[id], ...(blockOverride(pid, member?.type, id) || {}) }))
    .filter(b => b.fields && (!b.onlyIf || (member && member[b.onlyIf])));

  // Bloku za hiari ambazo hazijawashwa kwa mtu huyu (mf. chess kwa mzazi)
  const skipped = blockIds
    .map(id => ({ id, ...BLOCKS[id], ...(blockOverride(pid, member?.type, id) || {}) }))
    .filter(b => b.optional && b.onlyIf && member && !member[b.onlyIf]);

  function change(k, v) { setValues(prev => ({ ...prev, [k]: v })); }

  const allFields = [...visibleBlocks.flatMap(b => b.fields), ...COMMON_FIELDS];
  const overall = completionOf(values, allFields);

  async function submit() {
    const missing = allFields.filter(f => f.required && !values[f.key]);
    if (missing.length) {
      setErr(`Bado hujajaza: ${missing.map(f => f.label).join(', ')}`);
      window.scrollTo(0, 0);
      return;
    }
    setSaving(true); setErr('');
    try {
      const name = await resolveAuthorName(currentUser);
      await saveIndividualReport({
        programId: pid,
        member,
        weekKey: wk,
        weekLabel: weekLabel(weekKeyToDate(wk)),
        values,
        author: { uid: currentUser.uid, name },
        clubId: member.clubId,
        clubName: member.clubName,
      });
      setMsg('Ripoti imehifadhiwa.');
      setTimeout(() => navigate(`/programs/${pid}/week`), 900);
    } catch (e) {
      console.error(e);
      setErr('Imeshindikana kuhifadhi. Angalia mtandao kisha jaribu tena.');
    }
    setSaving(false);
  }

  async function submitContribution() {
    if (!contrib.trim()) return;
    setSaving(true);
    try {
      const name = await resolveAuthorName(currentUser);
      await addContribution(existing.id, { uid: currentUser.uid, name }, contrib.trim());
      setContrib('');
      setMsg('Nyongeza yako imewekwa kwenye ripoti hii.');
      load();
    } catch (e) { console.error(e); setErr('Imeshindikana kuweka nyongeza.'); }
    setSaving(false);
  }

  async function runAI() {
    if (!notes.trim()) { setErr('Andika maelezo mafupi kwanza.'); return; }
    setAiBusy(true); setErr('');
    try {
      const out = await draftFromNotes({
        member, programId: pid, notes,
        blockLabels: visibleBlocks.map(b => b.label),
      });
      const sum = out.split(/USHAURI:/i);
      const summary = (sum[0] || '').replace(/MUHTASARI:/i, '').trim();
      const advice = (sum[1] || '').trim();
      setValues(prev => ({
        ...prev,
        summary: summary || prev.summary,
        recommendation: advice || prev.recommendation,
      }));
      setShowAI(false);
      setMsg('AI imeandaa muhtasari — hakiki kisha rekebisha kama inahitajika.');
    } catch (e) { console.error(e); setErr('AI haipatikani kwa sasa.'); }
    setAiBusy(false);
  }

  if (loading) {
    return <ProgramShell program={program} tab="week"><div style={{ ...S.card, ...S.empty }}>Inapakia...</div></ProgramShell>;
  }
  if (!member) {
    return <ProgramShell program={program} tab="week"><div style={{ ...S.card, ...S.empty }}>Mwanachama hakupatikana.</div></ProgramShell>;
  }

  return (
    <ProgramShell program={program} tab="week">

      {/* Kichwa cha mtu */}
      <div style={{ ...S.card, borderLeft: `4px solid ${program.color}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{
            width: '44px', height: '44px', borderRadius: '13px', flexShrink: 0,
            background: `${program.color}15`, display: 'flex', alignItems: 'center',
            justifyContent: 'center', fontSize: '17px', fontWeight: 800, color: program.color,
          }}>
            {(member.fullName || '?').charAt(0).toUpperCase()}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontFamily: T.serif, fontSize: '18px', color: T.green }}>{member.fullName}</div>
            <div style={{ fontSize: '11.5px', color: T.muted, marginTop: '3px' }}>
              {MEMBER_TYPES[member.type]?.label} · {member.school || member.centre || member.area || '—'}
              {member.clubName ? ` · ${member.clubName}` : ''}
            </div>
          </div>
          <button style={{ ...S.btnGhost, padding: '7px 12px', fontSize: '11.5px' }}
            onClick={() => navigate(`/programs/${pid}/members/${mid}`)}>Wasifu</button>
        </div>
        <div style={{ fontSize: '12px', color: T.muted, marginTop: '13px', paddingTop: '13px', borderTop: `1px solid #F3F1EB` }}>
          Ripoti ya wiki: <strong style={{ color: T.ink }}>{weekLabel(weekKeyToDate(wk))}</strong> ({wk})
        </div>
      </div>

      {msg && <div style={S.ok}>{msg}</div>}
      {err && <div style={S.err}>{err}</div>}

      {/* ── Ripoti tayari imeandikwa na staff mwingine ── */}
      {readOnly && existing && (
        <div style={{ ...S.card, background: '#EFF6FF', border: '1px solid #BFDBFE' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <Icon name="info" size={16} color="#1565C0" />
            <strong style={{ fontSize: '13.5px', color: '#1E3A8A' }}>
              Ripoti hii imeshaandikwa na {existing.authorName}
            </strong>
          </div>
          <div style={{ fontSize: '12.5px', color: '#1E40AF', lineHeight: 1.7 }}>
            Isome hapa chini. Kama una la kuongeza, weka <strong>nyongeza</strong> mwishoni —
            usiandike ripoti nyingine ya mtu huyohuyo.
          </div>
          <div style={{ fontSize: '11px', color: '#3B82F6', marginTop: '8px' }}>
            Iliandikwa {fmtDateTime(existing.createdAt)}
          </div>
        </div>
      )}

      {/* ── Msaidizi wa AI ── */}
      {!readOnly && (
        <div style={{ ...S.card, padding: 0, overflow: 'hidden' }}>
          <div onClick={() => setShowAI(!showAI)} style={{
            display: 'flex', alignItems: 'center', gap: '10px', padding: '14px 18px', cursor: 'pointer',
          }}>
            <Icon name="sparkle" size={17} color={T.orange} />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '13.5px', fontWeight: 700, color: T.ink }}>Msaidizi wa AI</div>
              <div style={{ fontSize: '11.5px', color: T.muted, marginTop: '2px' }}>
                Andika kwa maneno yako — AI itaandaa muhtasari na ushauri
              </div>
            </div>
            <span style={{ color: T.muted, transform: showAI ? 'rotate(90deg)' : 'none' }}>›</span>
          </div>
          {showAI && (
            <div style={{ padding: '0 18px 18px' }}>
              <textarea
                style={{ ...S.input, minHeight: '90px', resize: 'vertical' }}
                placeholder="mf. Amehudhuria session zote, amejifunza fork, ameshinda michezo 2. Bado anakimbilia kucheza bila kufikiri. Nyumbani hali ni shwari."
                value={notes}
                onChange={e => setNotes(e.target.value)}
              />
              <button style={{ ...S.btn, marginTop: '12px', background: T.orange, opacity: aiBusy ? 0.6 : 1 }}
                disabled={aiBusy} onClick={runAI}>
                {aiBusy ? 'AI inafikiri...' : 'Tengeneza muhtasari'}
              </button>
            </div>
          )}
        </div>
      )}

      {/* ── Kiwango cha ukamilifu ── */}
      <div style={{ ...S.card, padding: '14px 18px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12.5px', marginBottom: '9px' }}>
          <span style={{ color: T.muted, fontWeight: 600 }}>Ukamilifu wa ripoti</span>
          <strong style={{ color: overall === 100 ? '#059669' : program.color }}>{overall}%</strong>
        </div>
        <div style={{ height: '7px', background: '#F0EEE7', borderRadius: '99px', overflow: 'hidden' }}>
          <div style={{ width: `${overall}%`, height: '100%', background: overall === 100 ? '#059669' : program.color }} />
        </div>
      </div>

      {/* ── Bloku za ripoti ── */}
      {visibleBlocks.map(b => (
        <BlockSection
          key={b.id}
          block={b}
          open={!!open[b.id]}
          onToggle={() => setOpen(o => ({ ...o, [b.id]: !o[b.id] }))}
          done={completionOf(values, b.fields)}
        >
          {readOnly
            ? <ReadOnlyBlock fields={b.fields} values={values} />
            : b.fields.map(f => (
              <Field key={f.key} field={f} value={values[f.key]} onChange={change} color={b.color} />
            ))}
        </BlockSection>
      ))}

      {skipped.length > 0 && (
        <div style={{ ...S.card, background: '#FAF8F3', fontSize: '12px', color: T.muted, lineHeight: 1.7 }}>
          <Icon name="info" size={13} color={T.orange} />{' '}
          {skipped.map(b => b.label).join(', ')} haijaulizwa hapa kwa sababu ni <strong>hiari</strong>
          {' '}kwa {MEMBER_TYPES[member.type]?.label?.toLowerCase()}. Kama {member.fullName} ameanza
          kujifunza chess, iwashe kwenye{' '}
          <span
            onClick={() => navigate(`/programs/${pid}/members/${mid}/edit`)}
            style={{ color: T.green, fontWeight: 700, cursor: 'pointer', textDecoration: 'underline' }}
          >wasifu wake</span>.
        </div>
      )}

      {/* ── Muhtasari & ushauri ── */}
      <div style={S.card}>
        <h2 style={S.h2}>Hali Halisi na Ushauri</h2>
        <div style={{ fontSize: '12px', color: T.muted, marginBottom: '15px', lineHeight: 1.7 }}>
          Maswali hapo juu yanakusanya alama. Hapa ndipo unaeleza kile ambacho
          alama haziwezi kukieleza — andika kwa uhuru.
        </div>
        {readOnly
          ? <ReadOnlyBlock fields={COMMON_FIELDS} values={values} />
          : COMMON_FIELDS.map(f => (
            <Field key={f.key} field={f} value={values[f.key]} onChange={change} color={program.color} />
          ))}
      </div>

      {/* ── Nyongeza za staff wengine ── */}
      {existing?.contributions?.length > 0 && (
        <div style={S.card}>
          <h2 style={S.h2}>Nyongeza za Staff Wengine ({existing.contributions.length})</h2>
          {existing.contributions.map((c, i) => (
            <div key={i} style={{ padding: '11px 0', borderTop: i ? `1px solid #F3F1EB` : 'none' }}>
              <div style={{ fontSize: '12px', fontWeight: 700, color: program.color }}>{c.byName}</div>
              <div style={{ fontSize: '13px', color: T.ink, marginTop: '4px', lineHeight: 1.6 }}>{c.text}</div>
              <div style={{ fontSize: '10.5px', color: T.muted, marginTop: '4px' }}>{fmtDateTime(c.at)}</div>
            </div>
          ))}
        </div>
      )}

      {existing && (
        <div style={S.card}>
          <h2 style={S.h2}>Ongeza Nyongeza</h2>
          <div style={{ fontSize: '12px', color: T.muted, marginBottom: '11px', lineHeight: 1.6 }}>
            Umeona kitu ambacho hakipo kwenye ripoti hii? Kiandike hapa badala ya kuandika ripoti mpya.
          </div>
          <textarea
            style={{ ...S.input, minHeight: '80px', resize: 'vertical', marginBottom: '12px' }}
            value={contrib}
            onChange={e => setContrib(e.target.value)}
            placeholder="mf. Nilimuona kwenye session ya Alhamisi, ameonesha kujiamini zaidi..."
          />
          <button style={{ ...S.btnGhost, opacity: saving ? 0.6 : 1 }} disabled={saving} onClick={submitContribution}>
            Weka nyongeza
          </button>
        </div>
      )}

      {/* ── Hifadhi ── */}
      {!readOnly && (
        <div style={{ display: 'flex', gap: '10px', marginBottom: '30px' }}>
          <button style={{ ...S.btn, background: program.color, flex: 1, opacity: saving ? 0.6 : 1 }}
            disabled={saving} onClick={submit}>
            {saving ? 'Inahifadhi...' : existing ? 'Hifadhi mabadiliko' : 'Hifadhi ripoti'}
          </button>
          <button style={S.btnGhost} onClick={() => navigate(`/programs/${pid}/week`)}>Ghairi</button>
        </div>
      )}

    </ProgramShell>
  );
}

function ReadOnlyBlock({ fields, values }) {
  const filled = fields.filter(f => values[f.key] !== '' && values[f.key] !== undefined && values[f.key] !== null);
  if (!filled.length) return <div style={S.empty}>Hakuna kilichojazwa hapa.</div>;
  return (
    <div>
      {filled.map(f => (
        <div key={f.key} style={{ marginBottom: '13px' }}>
          <div style={{ fontSize: '11.5px', color: T.muted, fontWeight: 700 }}>{f.label}</div>
          <div style={{ fontSize: '13.5px', color: T.ink, marginTop: '3px', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
            {f.type === 'rating'
              ? `${values[f.key]}/5 — ${RATING_LABELS[values[f.key]]}`
              : String(values[f.key])}
          </div>
        </div>
      ))}
    </div>
  );
}
